import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { getFilesFileIDDownloadUrl } from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { useAudioPlayerStore } from "Infrastructure/Stores/audioPlayerStore";
import { Spacing } from "Infrastructure/Themes/Themes";

interface ActorVoiceAudioPlayerProps {
  fileID: string;
  languageLabel: string;
  compact?: boolean;
}

function useFileDownloadUrlQuery(fileID: string) {
  return useQuery({
    queryKey: ["FILE_DOWNLOAD_URL", fileID],
    queryFn: processResponse(() => getFilesFileIDDownloadUrl(fileID)),
    staleTime: 5 * 60 * 1000,
  });
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const ActorVoiceAudioPlayer = ({
  fileID,
  languageLabel,
  compact = false,
}: ActorVoiceAudioPlayerProps): React.ReactNode => {
  const theme = useTheme();
  const instanceId = useId();

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const playingInstanceId = useAudioPlayerStore((s) => s.playingInstanceId);
  const setPlayingInstanceId = useAudioPlayerStore(
    (s) => s.setPlayingInstanceId,
  );

  const { data: urlData, isLoading } = useFileDownloadUrlQuery(fileID);

  // Stop this player when another instance starts playing
  useEffect(() => {
    if (isPlaying && playingInstanceId !== instanceId) {
      const audio = audioRef.current;
      if (audio) audio.pause();
      setIsPlaying(false);
      setProgress(0);
    }
  }, [playingInstanceId, instanceId, isPlaying]);

  const handleTogglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      if (playingInstanceId === instanceId) setPlayingInstanceId(null);
    } else {
      audio.play();
      setIsPlaying(true);
      setPlayingInstanceId(instanceId);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    if (playingInstanceId === instanceId) setPlayingInstanceId(null);
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const newTime = ((value as number) / 100) * audio.duration;
    audio.currentTime = newTime;
    setProgress(value as number);
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = (value as number) / 100;
    audio.volume = newVolume;
    setVolume(newVolume);
    if (newVolume > 0) setIsMuted(false);
  };

  const handleToggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = volume || 1;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  if (compact) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          paddingX: Spacing.XS,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
          {languageLabel}
        </Typography>
        {isLoading ? (
          <CircularProgress size={16} />
        ) : (
          <IconButton
            size="small"
            onClick={handleTogglePlay}
            disabled={!urlData}
            color="inherit"
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 16 }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
        {urlData && (
          // biome-ignore lint/a11y/useMediaCaption: audio preview player without transcript
          <audio
            ref={audioRef}
            src={urlData.downloadUrl}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onLoadedMetadata={handleLoadedMetadata}
          />
        )}
      </Box>
    );
  }

  return (
    <Box px={2}>
      <Typography
        variant="body2"
        sx={{ mb: Spacing.S, color: "text.secondary" }}
      >
        {languageLabel}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: Spacing.S,
          backgroundColor: theme.colors.backgroundInput,
          borderRadius: theme.spacing(Spacing.XS),
          paddingLeft: Spacing.S,
          paddingRight: Spacing.L,
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <IconButton
            size="small"
            onClick={handleTogglePlay}
            disabled={!urlData}
            color="inherit"
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        )}
        <Slider
          value={progress}
          onChange={handleSliderChange}
          disabled={!urlData || isLoading}
          size="small"
          sx={{ flex: 1 }}
        />
        <Typography
          variant="caption"
          sx={{
            minWidth: "38px",
            textAlign: "right",
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatTime(duration)}
        </Typography>
        <IconButton
          color="inherit"
          size="small"
          onClick={handleToggleMute}
          disabled={!urlData}
        >
          {isMuted || volume === 0 ? (
            <VolumeOffIcon sx={{ fontSize: 18 }} />
          ) : (
            <VolumeUpIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
        <Slider
          value={isMuted ? 0 : volume * 100}
          onChange={handleVolumeChange}
          disabled={!urlData || isLoading}
          size="small"
          sx={{ width: 72 }}
        />
      </Box>
      {urlData && (
        // biome-ignore lint/a11y/useMediaCaption: audio preview player without transcript
        <audio
          ref={audioRef}
          src={urlData.downloadUrl}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onLoadedMetadata={handleLoadedMetadata}
        />
      )}
    </Box>
  );
};
